(() => {
  const SELECTOR_SHARE = '[data-cart-share]';
  const SELECTOR_PDF = '[data-cart-save-pdf]';
  const BTN_BUSY_CLASS = 'is-busy';

  function isCartPage() {
    return window.location.pathname === '/cart';
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) {
      throw new Error('Unable to load cart');
    }
    return res.json();
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      button.classList.add(BTN_BUSY_CLASS);
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      return;
    }
    button.classList.remove(BTN_BUSY_CLASS);
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }

  function showStatus(message, isError) {
    const node = document.querySelector('[data-cart-quote-status]');
    if (!node) return;
    node.textContent = message || '';
    node.classList.toggle('is-error', !!isError);
  }

  function buildShareUrl(cart) {
    const qtyByVariant = new Map();
    (cart.items || []).forEach((item) => {
      const variantId = Number(item.variant_id);
      if (!variantId) return;
      const current = qtyByVariant.get(variantId) || 0;
      qtyByVariant.set(variantId, current + Number(item.quantity || 0));
    });

    const entries = Array.from(qtyByVariant.entries()).filter((entry) => entry[1] > 0);
    if (!entries.length) {
      throw new Error('Cart is empty');
    }

    const cartPath = `/cart/${entries.map((entry) => `${entry[0]}:${entry[1]}`).join(',')}`;
    return `${window.location.origin}${cartPath}`;
  }

  async function copyShareLink(url) {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Shared Cart', url });
        return 'shared';
      } catch (e) {
        if (e && e.name === 'AbortError') return 'cancelled';
      }
    }

    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
      return 'copied';
    }

    const input = document.createElement('input');
    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
    return 'copied';
  }

  function truncate(value, maxLen) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLen) return text;
    return `${text.slice(0, Math.max(0, maxLen - 3))}...`;
  }

  function firstText(root, selectors) {
    for (let i = 0; i < selectors.length; i += 1) {
      const node = root.querySelector(selectors[i]);
      if (!node) continue;
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    return '';
  }

  function moneyValue(text) {
    const value = parseFloat(String(text || '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(value) ? value : null;
  }

  function moneySymbol(text) {
    const match = String(text || '').match(/^\s*([^\d\s-]+)/);
    return (match && match[1]) || '$';
  }

  function toCents(value) {
    if (!Number.isFinite(Number(value))) return null;
    return Math.round((Number(value) + Number.EPSILON) * 100);
  }

  function formatMoneyFromCents(cents, symbol) {
    if (!Number.isFinite(Number(cents))) return '-';
    const negative = Number(cents) < 0;
    const abs = Math.abs(Number(cents));
    const units = (abs / 100).toFixed(2).split('.');
    units[0] = units[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = `${units[0]}.${units[1]}`;
    return `${negative ? '-' : ''}${symbol || '$'}${amount}`;
  }

  function readUnitPriceFromRow(row) {
    const priceTd = row.querySelector('td.cart-item__prices');
    if (!priceTd) return null;

    const directCandidates = [
      priceTd.querySelector('.cs_final_price'),
      priceTd.querySelector('.csapp-price .money:not(del)'),
      priceTd.querySelector('.money.cart_price'),
    ];

    for (let i = 0; i < directCandidates.length; i += 1) {
      const el = directCandidates[i];
      if (!el) continue;
      const value = moneyValue(el.textContent);
      if (value == null) continue;
      return { value, symbol: moneySymbol(el.textContent) };
    }

    const moneyEls = Array.from(priceTd.querySelectorAll('.money, .cs_final_price'));
    for (let i = moneyEls.length - 1; i >= 0; i -= 1) {
      const el = moneyEls[i];
      if (!el || el.closest('del') || el.tagName === 'DEL') continue;
      const value = moneyValue(el.textContent);
      if (value == null) continue;
      return { value, symbol: moneySymbol(el.textContent) };
    }

    return null;
  }

  function readLineTotalFromRow(row) {
    const candidates = [
      row.querySelector('td.cart-item__totals .cs_final_price'),
      row.querySelector('td.cart-item__totals .a_pplr_item_line_price .money:not(del)'),
      row.querySelector('td.cart-item__totals .money:not(del)'),
      row.querySelector('.cart_line_price .cs_final_price'),
      row.querySelector('.cart_line_price .money:not(del)'),
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const el = candidates[i];
      if (!el || el.closest('del') || el.tagName === 'DEL') continue;
      const value = moneyValue(el.textContent);
      if (value == null) continue;
      return { value, symbol: moneySymbol(el.textContent) };
    }

    return null;
  }

  function getShippingEstimateText() {
    const freeShip = firstText(document, ['#main-cart-footer .free-ship-note']);
    if (freeShip) {
      return freeShip;
    }

    const bestRateRow = document.querySelector('#calcResults .shipping-rate');
    if (bestRateRow) {
      const name = firstText(bestRateRow, ['.shipping-rate__name']);
      const timeRaw = firstText(bestRateRow, ['.shipping-rate__time']);
      const time = timeRaw.replace(/^[\.\u00b7\s]+/, '').trim();
      const price = firstText(bestRateRow, ['.shipping-rate__price']);
      const zipInput = document.getElementById('calcZip');
      const zip = zipInput ? String(zipInput.value || '').trim() : '';

      const parts = [];
      if (name) parts.push(name);
      if (time) parts.push(time);
      if (price) parts.push(price);

      if (parts.length) {
        return zip ? `Best option to ${zip}: ${parts.join(' - ')}` : `Best option: ${parts.join(' - ')}`;
      }
    }

    return 'Calculate shipping in cart for an estimated rate.';
  }

  function getHeaderLogoUrl() {
    // Use the white logo for PDF header consistency.
    return 'https://cdn.shopify.com/s/files/1/1971/3101/files/VICTORYSTOREcom-LOGO-white.png?v=1769802751';
  }

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  function wrapLines(ctx, text, maxWidth) {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    if (!words.length) return [''];

    const lines = [];
    let line = words[0];
    for (let i = 1; i < words.length; i += 1) {
      const test = `${line} ${words[i]}`;
      if (ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        lines.push(line);
        line = words[i];
      }
    }
    lines.push(line);
    return lines;
  }

  function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight) {
    const lines = wrapLines(ctx, text, maxWidth);
    lines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });
    return y + lines.length * lineHeight;
  }

  function dataUrlToBytes(dataUrl) {
    const base64 = dataUrl.split(',')[1] || '';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  function joinByteArrays(chunks) {
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    chunks.forEach((chunk) => {
      out.set(chunk, offset);
      offset += chunk.length;
    });
    return out;
  }

  function buildPdfFromJpeg(jpegBytes, imageWidth, imageHeight) {
    const encoder = new TextEncoder();
    const chunks = [];
    let offset = 0;
    const offsets = [0];

    function pushText(text) {
      const bytes = encoder.encode(text);
      chunks.push(bytes);
      offset += bytes.length;
    }

    function pushBytes(bytes) {
      chunks.push(bytes);
      offset += bytes.length;
    }

    const pageWidth = 612;
    const pageHeight = 792;
    const content = `q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/Im0 Do\nQ\n`;

    pushText('%PDF-1.4\n');

    offsets[1] = offset;
    pushText('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    offsets[2] = offset;
    pushText('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

    offsets[3] = offset;
    pushText('3 0 obj\n');
    pushText('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] ');
    pushText('/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\n');
    pushText('endobj\n');

    offsets[4] = offset;
    pushText(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} `);
    pushText('/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ');
    pushText(`/Length ${jpegBytes.length} >>\nstream\n`);
    pushBytes(jpegBytes);
    pushText('\nendstream\nendobj\n');

    const contentBytes = encoder.encode(content);
    offsets[5] = offset;
    pushText(`5 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n`);
    pushBytes(contentBytes);
    pushText('endstream\nendobj\n');

    const xrefOffset = offset;
    pushText('xref\n0 6\n0000000000 65535 f \n');
    for (let i = 1; i <= 5; i += 1) {
      pushText(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    pushText(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    return new Blob([joinByteArrays(chunks)], { type: 'application/pdf' });
  }

  function collectDomSnapshot() {
    const rows = Array.from(document.querySelectorAll('#main-cart-items .cart-item'));
    let domTotalCents = 0;
    let domTotalCurrency = '';

    const items = rows.map((row) => {
      const name = firstText(row, ['.cart-item__name']);
      const variant = firstText(row, [
        '.product-option dd',
      ]);
      const qtyInput = row.querySelector('.quantity__input');
      const quantity = qtyInput ? Number(qtyInput.value || 0) : 0;

      const unit = readUnitPriceFromRow(row);
      const unitCents = unit ? toCents(unit.value) : null;
      const computedLineCents = unitCents != null ? unitCents * Math.max(0, quantity) : null;

      const domLine = readLineTotalFromRow(row);
      const lineCents = computedLineCents != null ? computedLineCents : (domLine ? toCents(domLine.value) : null);
      const lineSymbol = (unit && unit.symbol) || (domLine && domLine.symbol) || '$';

      if (lineCents != null) {
        domTotalCents += lineCents;
        if (!domTotalCurrency) domTotalCurrency = lineSymbol;
      }

      const fallbackUnitText = firstText(row, ['.cart-item__prices .money']);
      const fallbackLineText = firstText(row, ['.cart-item__totals .money:not(del)', '.cart_line_price .money:not(del)']);
      const unitPrice = unitCents != null
        ? formatMoneyFromCents(unitCents, unit.symbol)
        : (fallbackUnitText || '-');
      const lineTotal = lineCents != null
        ? formatMoneyFromCents(lineCents, lineSymbol)
        : (fallbackLineText || '-');

      return {
        name: name || 'Item',
        variant: variant && variant !== 'Default Title' ? variant : '',
        quantity,
        unitPrice: unitPrice || '-',
        lineTotal: lineTotal || '-',
      };
    });

    const aiodTotal = firstText(document, [
      '#main-cart-footer .csapps-cart-total .money',
      '#main-cart-footer .csapps-cart-total',
    ]);
    const themeTotal = firstText(document, [
      '#main-cart-footer .totals__total-value',
    ]);
    const domTotal = domTotalCents > 0 ? formatMoneyFromCents(domTotalCents, domTotalCurrency || moneySymbol(aiodTotal || themeTotal)) : '';

    return {
      items,
      itemCount: items.reduce((sum, item) => sum + Math.max(0, Number(item.quantity || 0)), 0),
      estimatedTotal: domTotal || aiodTotal || themeTotal || '-',
      shippingEstimate: getShippingEstimateText(),
    };
  }

  async function buildPdfBlob(snapshot) {
    const date = new Date().toLocaleString();
    const maxRows = 24;
    const items = snapshot.items.slice(0, maxRows);
    const omitted = snapshot.items.length > maxRows;

    const pageWidth = 612;
    const pageHeight = 792;
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = pageWidth * scale;
    canvas.height = pageHeight * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas not available');
    }
    ctx.scale(scale, scale);

    const tableLeft = 36;
    const tableRight = 576;
    const colQty = 430;
    const colUnit = 470;
    const colTotal = 520;
    const qtyCenter = Math.round((colQty + colUnit) / 2);
    const unitRight = colTotal - 10;
    const totalRight = tableRight - 10;

    const yPdfToCanvas = (y) => pageHeight - y;
    const fillRectPdf = (x, y, w, h) => ctx.fillRect(x, pageHeight - y - h, w, h);
    const fillTextPdf = (text, x, y) => ctx.fillText(text, x, yPdfToCanvas(y));
    const drawImagePdf = (img, x, y, w, h) => ctx.drawImage(img, x, pageHeight - y - h, w, h);
    const linePdf = (x1, y1, x2, y2) => {
      ctx.moveTo(x1, yPdfToCanvas(y1));
      ctx.lineTo(x2, yPdfToCanvas(y2));
    };
    const drawWrappedTextPdf = (text, x, y, maxWidth, lineHeight) => {
      const lines = wrapLines(ctx, text, maxWidth);
      lines.forEach((line, i) => {
        fillTextPdf(line, x, y - i * lineHeight);
      });
      return y - lines.length * lineHeight;
    };

    // Base background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    // Header bars
    ctx.fillStyle = '#27367f';
    fillRectPdf(36, 740, 540, 30);
    ctx.fillStyle = '#d6dbe9';
    fillRectPdf(36, 710, 540, 28);

    // Header rule lines
    ctx.strokeStyle = '#c4c4c4';
    ctx.lineWidth = 1;
    ctx.beginPath();
    linePdf(tableLeft, 710, tableRight, 710);
    linePdf(tableLeft, 682, tableRight, 682);
    ctx.stroke();

    // Header logo + title
    let logoDrawn = false;
    try {
      const logo = await loadImage(getHeaderLogoUrl());
      const targetH = 20;
      const targetW = Math.max(70, logo.width * (targetH / logo.height));
      drawImagePdf(logo, 46, 745, targetW, targetH);
      logoDrawn = true;
    } catch (e) {
      logoDrawn = false;
    }

    ctx.fillStyle = '#ffffff';
    const headerBaselineY = 750;
    ctx.textBaseline = 'alphabetic';
    ctx.font = 'bold 13px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    fillTextPdf('Order Estimate', (tableLeft + tableRight) / 2, headerBaselineY);
    ctx.textAlign = 'right';
    ctx.font = 'bold 8px Helvetica, Arial, sans-serif';
    fillTextPdf(date, 566, headerBaselineY);

    // Body/table text uses middle alignment for clean row centering.
    ctx.textBaseline = 'middle';

    // Table header text
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'left';
    ctx.font = 'bold 10px Helvetica, Arial, sans-serif';
    fillTextPdf('Item', 46, 724);
    ctx.textAlign = 'center';
    fillTextPdf('Qty', qtyCenter, 724);
    ctx.textAlign = 'right';
    fillTextPdf('Unit', unitRight, 724);
    fillTextPdf('Total', totalRight, 724);

    const bodyTopY = 682;
    const rowHeight = 24;

    // Product rows use a consistent row height.
    const bodyRows = items.length;
    const totalRowTopY = bodyTopY - bodyRows * rowHeight;
    const totalRowBottomY = totalRowTopY - rowHeight;

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const primary = truncate(item.name, 58);
      const secondary = item.variant ? ` - ${truncate(item.variant, 28)}` : '';
      const title = truncate(`${primary}${secondary}`, 70);
      const rowTopY = bodyTopY - rowHeight * i;
      const rowCenterY = rowTopY - rowHeight / 2;

      ctx.fillStyle = '#000000';
      ctx.font = '400 9px Helvetica, Arial, sans-serif';
      ctx.textAlign = 'left';
      fillTextPdf(title, 46, rowCenterY);
      ctx.textAlign = 'center';
      fillTextPdf(String(item.quantity || 0), qtyCenter, rowCenterY);
      ctx.textAlign = 'right';
      fillTextPdf(item.unitPrice, unitRight, rowCenterY);
      fillTextPdf(item.lineTotal, totalRight, rowCenterY);

      ctx.beginPath();
      linePdf(tableLeft, rowTopY - rowHeight, tableRight, rowTopY - rowHeight);
      ctx.stroke();
    }

    const totalTextY = totalRowTopY - rowHeight / 2;
    const hasComputedShipping = !/calculate shipping in cart/i.test(snapshot.shippingEstimate);
    const shippingLine = hasComputedShipping
      ? `Shipping Estimate: ${snapshot.shippingEstimate}`
      : 'Shipping Estimate: Use the Estimate Shipping tool in cart before downloading to include a shipping rate.';

    // Keep right-side columns tight to data + total rows.
    ctx.beginPath();
    linePdf(colQty, 710, colQty, totalRowBottomY);
    linePdf(colUnit, 710, colUnit, totalRowBottomY);
    linePdf(colTotal, 710, colTotal, totalRowBottomY);
    linePdf(tableLeft, totalRowTopY, tableRight, totalRowTopY);
    linePdf(tableLeft, totalRowBottomY, tableRight, totalRowBottomY);
    ctx.stroke();

    const footerY = Math.max(84, totalRowBottomY - 16);
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 11px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'left';
    fillTextPdf('Estimated Total', 46, totalTextY);
    ctx.textAlign = 'right';
    ctx.font = 'bold 9px Helvetica, Arial, sans-serif';
    fillTextPdf(snapshot.estimatedTotal, totalRight, totalTextY - 0.5);

    ctx.font = '400 9px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'left';
    let textY = footerY;
    textY = drawWrappedTextPdf(truncate(shippingLine, 130), 46, textY, 530, 13);
    textY -= 26; // Move disclaimer down by two lines for separation.
    ctx.textAlign = 'center';
    textY = drawWrappedTextPdf(
      'This estimate is not a formal quote. Taxes and discounts are applied at checkout. Prices and shipping are subject to change.',
      306,
      textY,
      530,
      13
    );
    if (omitted) {
      ctx.textAlign = 'left';
      drawWrappedTextPdf('Additional lines were omitted from this PDF. Use Share Cart for the full cart.', 46, textY, 530, 13);
    }

    const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.93);
    const jpegBytes = dataUrlToBytes(jpegDataUrl);
    return buildPdfFromJpeg(jpegBytes, canvas.width, canvas.height);
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function onShareClick(button) {
    setBusy(button, true);
    showStatus('');
    try {
      const cart = await getCart();
      const url = buildShareUrl(cart);
      const state = await copyShareLink(url);

      if (state === 'shared') {
        showStatus('Cart link shared.');
      } else if (state === 'cancelled') {
        showStatus('');
      } else {
        showStatus('Share link copied to clipboard.');
      }
    } catch (e) {
      showStatus('Unable to share cart right now.', true);
    } finally {
      setBusy(button, false);
    }
  }

  async function onPdfClick(button) {
    setBusy(button, true);
    showStatus('');
    try {
      const snapshot = collectDomSnapshot();
      if (!snapshot.items.length || !snapshot.itemCount) {
        throw new Error('Cart is empty');
      }

      const blob = await buildPdfBlob(snapshot);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `victorystore-order-estimate-${stamp}.pdf`);
      showStatus('PDF downloaded with current cart pricing.');
    } catch (e) {
      const isEmpty = e && /empty/i.test(String(e.message || ''));
      showStatus(isEmpty ? 'Add items to cart before downloading a PDF.' : 'Unable to download PDF right now.', true);
    } finally {
      setBusy(button, false);
    }
  }

  document.addEventListener('click', (event) => {
    if (!isCartPage()) return;

    const shareButton = event.target.closest(SELECTOR_SHARE);
    if (shareButton) {
      event.preventDefault();
      onShareClick(shareButton);
      return;
    }

    const pdfButton = event.target.closest(SELECTOR_PDF);
    if (pdfButton) {
      event.preventDefault();
      onPdfClick(pdfButton);
    }
  });
})();
